import { AdditionalSignOffInformation } from "@/Common/Components/Modals/LinkingPopup/LinkingPopup";
import { MyleTableColumn } from "@/Common/Components/Tables/MyleTableColumn";
import { EmployeeTitleHelper } from "@/Common/Helpers/EmployeeTitleHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { Person } from "@/Common/Models/Person/Person";
import { PortfolioNotesSortOrderType, RedirectTo, SortDirection } from "@/Common/Models/UI/Enums";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { ApssClient } from "@/Patient/Api/Apss/ApssClient";
import { EnumCnesstSendStatus } from "@/Patient/Models/Cnesst/EnumCnesstSendStatus";
import { Annexes, PendingApprovalsItem } from "@/Portfolio/Models/MyNotesInboxItem";
import { PortfolioStore } from "@/Portfolio/Stores/PortfolioStore";

class PendingApprovalInboxHandler extends BaseOperationHandler {
    private readonly helper: MyleHelper = new MyleHelper();

    constructor() {
        super();

        this.RegisterOperation(LocalOperations.Portfolio.PendingApproval_Reload, this.reload);
        this.RegisterOperation(LocalOperations.Portfolio.PendingApproval_SearchTextChanged, this.searchTextChanged);
        this.RegisterOperation(LocalOperations.Portfolio.PendingApproval_OnEncounterClick, this.onEncounterClick);
        this.RegisterOperation(LocalOperations.Portfolio.PendingApproval_SetSelectedEncounterInfo, (): void => {});
        this.RegisterOperation(LocalOperations.Portfolio.PendingApproval_OnModifyEncounterClick, this.onModifyEncounterClick);
        this.RegisterOperation(LocalOperations.Portfolio.PendingApproval_OnNotesPopupOpen, this.onNotesPopupOpen);
        this.RegisterOperation(LocalOperations.Portfolio.PendingApproval_ClearSelectedSupervisor, this.clearSelectedSupervisor);
        this.RegisterOperation(LocalOperations.Portfolio.PendingApproval_CloseApprovalSupervisorPopup, this.closeApprovalSupervisorPopup);
        this.RegisterOperation(LocalOperations.Portfolio.PendingApproval_SelectResidentSupervisor, this.selectResidentSupervisor);
        this.RegisterOperation(LocalOperations.Portfolio.PendingApproval_OnSendClick, this.onSendClick);
        this.RegisterOperation(LocalOperations.Portfolio.PendingApproval_OnResidentMessageChange, this.onResidentMessageChange);
        this.RegisterOperation(LocalOperations.Portfolio.PendingApproval_OnApprovePendingApprovalClick, this.onApprovePendingApprovalClick);
        this.RegisterOperation(LocalOperations.Portfolio.PendingApproval_OnReturnPendingApprovalClick, this.onReturnPendingApprovalClick);
        this.RegisterOperation(LocalOperations.Portfolio.PendingApproval_OnReturnPendingApprovalMessageChange, this.onReturnPendingApprovalMessageChange);
        this.RegisterOperation(LocalOperations.Portfolio.PendingApproval_OnApprovePopupClick, this.onApprovePopupClick);
        this.RegisterOperation(LocalOperations.Portfolio.PendingApproval_OnReturnPopupClick, this.onReturnPopupClick);
        this.RegisterOperation(LocalOperations.Portfolio.TogglePDFSideBar, this.togglePdfSideBar);
        this.RegisterOperation(LocalOperations.Misc.Table_ColumnSortClick, this.setColumnSort);
    }

    private reload(request: LocalEvent, store: PortfolioStore): void {
        store.PortfolioActions.LoadPendingApproval(store.PendingApprovalInboxModel);
    }

    private searchTextChanged(request: LocalEvent, store: PortfolioStore): void {
        store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("SearchText", request.Get<string>("value"));
        store.PortfolioActions.LoadPendingApproval(store.PendingApprovalInboxModel);
    }

    private onEncounterClick(request: LocalEvent, store: PortfolioStore): void {
        let tableTarget: MyleTableTarget;
        tableTarget = request.Get<MyleTableTarget>("target");

        if (tableTarget == MyleTableTarget.Myle_PortfolioPendingApproval_RowClick) {
            let rowData = request.Get<PendingApprovalsItem>("rowData");
            if (rowData != null) {
                store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("SelectedPendingApprovalPatientContactId", rowData.PatientContactId);
                store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("SelectedPendingApprovalSupervisorContactId", rowData.SupervisorContactId);
                store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("SelectedPendingApprovalStatus", rowData.Status);
                store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("SelectedPendingApprovalEncounterId", rowData.EncounterId);

                // get current index of selected, so we can move to next one if this encounter is returned (and thus won't reappear in grid when re-navigating to portfololio)
                // we'll ust that to automaticaly select the next note
                let previousSelectedIndex = -1;
                let models = store.PendingApprovalInboxModel.get("TableData").toArray();
                for (let i = 0; i < models.length; i++) {
                    if (models[i].Model == rowData) {
                        previousSelectedIndex = i;
                        break;
                    }
                }
                store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("SelectedNoteIndex", previousSelectedIndex);

                store.PortfolioActions.GeneratePendingApprovalNotePDF(rowData.EncounterId, rowData.PatientContactId);
            }
        }

        store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("PendingNotesPopup", false);
    }

    private onModifyEncounterClick(request: LocalEvent, store: PortfolioStore): void {
        var supervisorContactId = store.PendingApprovalInboxModel.get("ApprovalSupervisorSelectedClinicians").toArray()[0].ContactId;
        var residentContactId = sessionStore.getCurrentEmployee().ContactId;
        var encounterId = store.PendingApprovalInboxModel.get("SelectedPendingApprovalEncounterId");
        var message = store.PendingApprovalInboxModel.get("ApprovalResidentMessage");

        store.PortfolioActions.SendPendingApproval(supervisorContactId, residentContactId, encounterId, message);
        window.setTimeout(() => {
            let openPatientOperation = new LocalEvent(LocalOperations.Misc.RedirectToTab);

            openPatientOperation.AddItem("value", store.PendingApprovalInboxModel.get("SelectedPendingApprovalPatientContactId"));
            openPatientOperation.AddItem("target", RedirectTo.Patient);
            openPatientOperation.AddItem("currentLocation", RouterHistory.location);
            openPatientOperation.AddItem("OpenEncounterId", store.PendingApprovalInboxModel.get("SelectedPendingApprovalEncounterId"));

            if (EmployeeTitleHelper.isStudent(sessionStore.myleEmployeeTitle)) {
                openPatientOperation.AddItem("encounterEdit", true);
            }
            else {
                openPatientOperation.AddItem("encounterEdit", false);
            }

            LocalEventDispatcher.dispatch(openPatientOperation);
        }, 100);
    }

    private onNotesPopupOpen(request: LocalEvent, store: PortfolioStore): void {
        let encounterId = request.Get<string>("encounterId");
        let patientContactId = request.Get<string>("patientContactId");
        let supervisorContactId = request.Get<string>("employeeContactId");
        let status = request.Get<number>("status");

        store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("ApprovalResidentMessage", "");
        store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("SelectedPendingApprovalSupervisorContactId", supervisorContactId);
        store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("SelectedPendingApprovalPatientContactId", patientContactId);
        store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("SelectedPendingApprovalStatus", status);
        // store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("SelectedPendingApprovalEncounterId", encounterId);

        store.PortfolioActions.GetPendingApprovalNotes(encounterId, patientContactId);

        //   sessionStore.showGlobalOverlay(true);
    }

    private clearSelectedSupervisor(request: LocalEvent, store: PortfolioStore): void {
        store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("ApprovalSupervisorSelectedClinicians", Immutable.List([]));
        store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("ApprovalSupervisorClinicianName", "");
    }

    private closeApprovalSupervisorPopup(request: LocalEvent, store: PortfolioStore): void {
        store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("PendingNotesPopup", false);
    }

    private selectResidentSupervisor(request: LocalEvent, store: PortfolioStore): void {
        let a = request.Get<Person>("item");
        store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("ApprovalSupervisorSelectedClinicians", Immutable.List([a]));
        store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("ApprovalSupervisorClinicianName", a.NameWithPrefix);
    }

    private onSendClick(request: LocalEvent, store: PortfolioStore): void {
        let supervisorContactId = store.PendingApprovalInboxModel.get("ApprovalSupervisorSelectedClinicians").toArray()[0].ContactId;
        let residentContactId = sessionStore.getCurrentEmployee().ContactId;
        let encounterId = store.PendingApprovalInboxModel.get("SelectedPendingApprovalEncounterId");
        let message = store.PendingApprovalInboxModel.get("ApprovalResidentMessage");

        store.PortfolioActions.SendPendingApproval(supervisorContactId, residentContactId, encounterId, message);
    }

    private onResidentMessageChange(request: LocalEvent, store: PortfolioStore): void {
        var value = request.Get<string>("value");

        store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("ApprovalResidentMessage", value);
    }

    private async onApprovePendingApprovalClick(request: LocalEvent, store: PortfolioStore): Promise<void> {
        const forms = request.Get<Annexes[]>("forms");
        const info = request.Get<AdditionalSignOffInformation>("additionalSignOffInformation");

        // get unsent forms
        const unsentApssForms = forms.filter(form => form.Type === "APSS" && this.isApssFormUnsent(form));
        const unsentCnesstForms = forms.filter(form => form.Type === "CNESST" && form.Status !== EnumCnesstSendStatus[EnumCnesstSendStatus.Success]);

        // sync unsent APSS forms
        for (let form of unsentApssForms) {
            try {
                const remoteForm = await ApssClient.fetchForm(parseInt(form.Id), null /* Use activeSite */);
                form.Status = remoteForm.ReqReg_Etat;
                form.IsUpdated = true;
            }
            catch (error) {
                form.IsUpdated = false;
                sessionStore.showPopup(error);
            }
        }

        // check for unsent forms
        const unsentForms =
            (
                unsentApssForms
                    .filter(form => this.isApssFormUnsent(form)) // update after sync
                    .map(form => cacheStore.GetString("APSSLABEL") + ": " + form.Name)
            )
                .concat(
                    unsentCnesstForms
                        .map(form => cacheStore.GetString("Cnesst") + ": " + form.Name)
                );
        if (unsentForms.any()) {
            sessionStore.showPopup({
                title: cacheStore.GetString("UnsentEncounterFormsLabel"),
                content: [cacheStore.GetString("UnsentEncounterFormsWarning")].concat(unsentForms),
                contentStyle: "list-after-first"
            });
            return;
        }

        // approve
        store.PortfolioActions.ApprovePendingApproval(
            store.PendingApprovalInboxModel.get("SelectedPendingApprovalEncounterId"),
            store.PendingApprovalInboxModel.get("ReturnPendingApprovalMessage"),
            info
        );
    }

    private onReturnPendingApprovalClick(request: LocalEvent, store: PortfolioStore): void {
        store.PortfolioActions.ReturnPendingApproval(store.PendingApprovalInboxModel.get("SelectedPendingApprovalEncounterId"), store.PendingApprovalInboxModel.get("ReturnPendingApprovalMessage"));
    }

    private onReturnPendingApprovalMessageChange(request: LocalEvent, store: PortfolioStore): void {
        store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("ReturnPendingApprovalMessage", request.Get<string>("value"));
    }

    private onApprovePopupClick(request: LocalEvent, store: PortfolioStore): void {
        const info = request.Get<AdditionalSignOffInformation>("additionalSignOffInformation");

        store.PortfolioActions.ApprovePendingApproval(
            store.PendingApprovalInboxModel.get("SelectedPendingApprovalEncounterId"),
            store.PendingApprovalInboxModel.get("ApprovalResidentMessage"),
            info
        );
    }

    private onReturnPopupClick(request: LocalEvent, store: PortfolioStore): void {
        store.PortfolioActions.ReturnPendingApproval(store.PendingApprovalInboxModel.get("SelectedPendingApprovalEncounterId"), store.PendingApprovalInboxModel.get("ApprovalResidentMessage"));
    }

    public setColumnSort(request: LocalEvent, store: PortfolioStore): void {
        let tableTarget = request.Get<MyleTableTarget>("target");

        if (tableTarget === MyleTableTarget.Myle_PortfolioMyNotes_Resident_Search_ColumnSortClick) {
            let columnOrderType = request.Get<PortfolioNotesSortOrderType>("sortColumn");

            if (store.PendingApprovalInboxModel.get("OrderByColumn") && store.PendingApprovalInboxModel.get("OrderByColumn") === columnOrderType) {
                if (store.PendingApprovalInboxModel.get("OrderByDirection") === SortDirection.ASC)
                    store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("OrderByDirection", SortDirection.DESC);
                else
                    store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("OrderByDirection", SortDirection.ASC);
            }
            else {
                store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("OrderByColumn", columnOrderType);
                store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("OrderByDirection", SortDirection.ASC);
            }
            let column = request.Get<MyleTableColumn>("columnObject");

            if (column) {
                if (column.columnOptions.IsSortActive) {
                    column.ToggleSortDirection();
                }
                column.columnOptions.IsSortActive = true;
            }

            store.PortfolioActions.LoadPendingApproval(store.PendingApprovalInboxModel);
        }
    }

    private togglePdfSideBar(request: LocalEvent, store: PortfolioStore): void {
        let isPdfNoteOpen = store.PendingApprovalInboxModel.get("PdfNoteOpen");
        store.PendingApprovalInboxModel = store.PendingApprovalInboxModel.set("PdfNoteOpen", !isPdfNoteOpen);
    }

    private isApssFormUnsent(form: Annexes): boolean {
        return ["Non verifiee", "A completer"].some(status => status === this.helper.RemoveDiacritics(form.Status.toString()));
    }
}

export { PendingApprovalInboxHandler };
